import { injectable } from 'inversify';
import { idGenerator } from '../../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { ObjectManager, ObjectUpdateCallback } from '../../../../concepts/objects';
import { UniversalSurfaceRenderer } from '../../../../platform/UniversalSurfaceRenderer';
import { Matrix } from '../../../../platform/geom/Matrix';
import { Point } from '../../../../platform/geom/Point';
import { Polyline } from '../../../../platform/geom/Polyline';
import { Rect } from '../../../../platform/geom/Rect';
import { Colors } from '../../../../ui';
import { resolve } from '../../../Container';
import { GuideLine, Physical, RelativeTo } from '../../entities/Physical';
import { HistoryManager } from '../../history/HistoryManager';
import { Transformation } from './Transformation';

const nextId = idGenerator();

export const Snap = (guide: GuideLine, vector: Point, bounds: Rect) => ({ guide, vector, bounds, distance: vector.length() });
export type Snap = ReturnType<typeof Snap>;

@injectable()
export abstract class SelectionTransformation extends Transformation {

    @resolve(CommandDispatcher) protected dispatch: CommandDispatcher;
    @resolve(HistoryManager) protected history: HistoryManager;
    @resolve(ObjectManager) protected objects: ObjectManager;

    public readonly id: string = nextId();
    public abstract get type(): string;

    private explicitKey = null as string;

    public get key() {
        return this.explicitKey ?? `${this.type}:${this.id}`;
    }

    public set key(value: string) {
        this.explicitKey = value;
    }

    protected rollback(): void {
        this.history.cancelNextIfKey(this.key);
    }

    protected update(callback: ObjectUpdateCallback<void>) {
        this.objects.update(u => {
            u.set('key', this.key);
            u.set('rolling', true); // Sometimes it won't be, but this will be detected
            callback(u);
        });
    }

    protected querySnapsFor(sourceGuides: GuideLine[]) {

        // Set of elements that can be snapped to
        const candidates = this.findSnappingCandidatesFor(sourceGuides);

        const allSnaps = [] as Snap[];
        for (const sg of sourceGuides) {
            const { guide: tg, rect: bounds } = this.findNearestGuide(sg, candidates) ?? {};

            if (!tg) continue;

            if (tg.axis === 'x') {
                const vector = new Point(0, tg.position - sg.position);
                allSnaps.push(Snap(
                    tg,
                    vector,
                    bounds,
                ));
            }
            else {
                const vector = new Point(tg.position - sg.position, 0);
                allSnaps.push(Snap(
                    tg,
                    vector,
                    bounds,
                ));
            }
        }

        // for (const l of allSnaps) {
        //     DebugOverlay.polyline(uuid(), l.line.toArray());
        // }

        // Find the snap with least distance
        const sortedSnaps = allSnaps.sort((a, b) => a.distance - b.distance);

        // Return the snap(s) with the least distance, if there are multiple snaps with the same distance return all of them
        const xSnaps = sortedSnaps.filter(s => s.guide.axis === 'x').filter((x, _, a) => x.distance === a[0].distance);
        const ySnaps = sortedSnaps.filter(s => s.guide.axis === 'y').filter((x, _, a) => x.distance === a[0].distance);
        return [...xSnaps, ...ySnaps];
    }

    protected findNearestGuide(source: GuideLine, candidates: Set<Physical>) {

        const dist = (a: GuideLine, b: GuideLine) => Math.abs(a.position - b.position);

        const targets = [] as { guide: GuideLine, rect: Rect, distance: number }[];
        for (const c of candidates) {
            const cBounds = c.bounds(RelativeTo.Model);
            const guides = c.queryGuides().filter(x => x.axis === source.axis && dist(source, x) <= 5);

            for (const g of guides) {
                const distance = Math.abs(g.position - source.position);

                targets.push({
                    guide: g,
                    rect: cBounds,
                    distance,
                });
            }
        }

        // targets.sort((a, b) => a.distance - b.distance);
        // return targets[0];

        const group = targets.filter(x => x.distance === targets[0].distance);
        if (group.length >= 1) {
            return {
                guide: group[0].guide,
                rect: Rect.fromMany(group.map(x => x.rect)),
                distance: group[0].distance,
            };
        }

        return null;
    }

    protected findSnappingCandidatesFor(guides: GuideLine[]) {
        return new Set<Physical>();
    }

    protected paintSnaps(gfx: UniversalSurfaceRenderer, mvm: Matrix, subjectBounds: Rect, snaps: Snap[]) {
        gfx.lineWidth = 1.5;
        gfx.strokeStyle = Colors.mystery;
        gfx.fillStyle = Colors.mystery;

        for (const s of snaps) {

            const targetBounds = s.bounds;

            const line = new Polyline([
                ...GuideLine.plot(s.guide, subjectBounds),
                ...GuideLine.plot(s.guide, targetBounds),
            ]);

            line.lines.forEach(l => {
                const ln = mvm.apply(l);

                gfx.fillCircle(ln.p1.x, ln.p1.y, 2);

                gfx.beginPath();
                gfx.moveTo(ln.p1.x, ln.p1.y);
                gfx.lineTo(ln.p2.x, ln.p2.y);
                gfx.stroke();


                if (l === line.lines[line.lines.length - 1]) {
                    gfx.fillCircle(ln.p2.x, ln.p2.y, 2);
                }

            });
        }
    }
}
